import React, {useEffect, useRef, useState} from 'react';
import UserAvatar from "../Item/UserAvatar";
import IconLink from "../Item/IconLink";
import {BROKER, VENDOR} from "../../services/constants/CompanyType";
import {
    ADMIN, ADMIN_LABEL, BILLING, BILLING_LABEL,
    MARKETING, MARKETING_LABEL, NOT_FOUND, SALES, SALES_LABEL
} from "../../services/constants/VendorAccountPrivilegues";
import {IS_PROFESSIONAL} from "../../services/constants/Vendor/VendorCompany";
import {
    ADMIN_BROKER, ADMIN_BROKER_LABEL, PRIMARY_ADMIN, PRIMARY_ADMIN_LABEL,
    PROJECT_ADMIN, PROJECT_ADMIN_LABEL, USER, USER_LABEL, USER_RESEARCH_ONLY,
    USER_RESEARCH_ONLY_LABEL, USER_VIEW_ALL, USER_VIEW_ALL_LABEL
} from "../../services/constants/BrokerAccountPrivilegues";
import _ from 'lodash';

const FF_NG_RFI = process.env.REACT_APP_FF_NG_RFI === '1';

const APP_BASE_URL = process.env.REACT_APP_APP_BASE_URL;

const UserMenu = ({userData, baseUrl, company}) => {
    let userMenu = [];

    switch (userData.type) {
        case BROKER:
            userMenu.push({
                icon: 'User', text: 'My profile', href: `${baseUrl}/account/settings?view=personal-info`,
            });
            if (userData.is_primary || ([ADMIN].includes(company.privileges))) {
                userMenu.push({
                    icon: 'Settings', text: 'Account settings', href: `${baseUrl}/account/settings?view=company-info`,
                });
            }
            userMenu.push({
                icon: 'Download', text: 'Downloads', href: `${baseUrl}/account/settings?view=downloads`,
            });
            if (FF_NG_RFI) {
                userMenu.push({
                    icon: 'RefreshCw', text: 'Vendor update requests', href: `${APP_BASE_URL}/broker/task-notification`,
                });
            } else {
                userMenu.push({
                    icon: 'RefreshCw', text: 'Vendor update requests', href: `/broker/update-requests`,
                });
            }
            if (userData.is_primary) {
                userMenu.push({
                    icon: 'Key', text: 'Generate API Key', href: `${baseUrl}/account/generate_api_key`,
                });
            }
            if (FF_NG_RFI) {
                if (userData.is_primary || ([ADMIN].includes(company.privileges))) {
                    userMenu.push({
                        icon: 'FileText', text: 'Custom RFIs', href: `${APP_BASE_URL}/broker/rfi/list`,
                    });
                }
            }
            userMenu.push({
                icon: 'Mail', text: 'Contact', href: `${baseUrl}/help`,
            });
            userMenu.push({
                icon: 'LogOut', text: 'Log out', href: `${APP_BASE_URL}/account/logout`,
            });
            break;
        case VENDOR:
            if ([ADMIN].includes(company.privileges) || userData.is_primary) {
                userMenu.push({
                    icon: 'User', text: 'Account', href: `${baseUrl}/account/index`,
                });
            }
            userMenu.push({
                icon: 'Settings', text: 'My Settings', href: `${APP_BASE_URL}/account/update`,
            });
            if ([ADMIN, MARKETING].includes(company.privileges) || userData.is_primary) {
                userMenu.push({
                    icon: 'CreditCard', text: 'Plan and Payment Settings', href: `${baseUrl}/plan-and-payment-settings`,
                });
            }
            userMenu.push({
                icon: 'HelpCircle', text: 'Help Center', href: `https://help.myshortlister.com/`,
            });
            userMenu.push({
                icon: 'LogOut', text: 'Log out', href: `${APP_BASE_URL}/account/logout`,
            });
            break;
        default:
            userData = [];
    }

    const [isOpen, setIsOpen] = useState(false);
    const userMenuRef = useRef();

    useEffect(() => {
        let handleClickOutside = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    });

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const showUpgradePlan = userData.type === VENDOR && !company?.[IS_PROFESSIONAL] && ([ADMIN, MARKETING].includes(company?.privileges) || userData.is_primary);

    function preserveHyphenCamelCase(str) {
        return str.split('-').map(part => _.camelCase(part)).join('-');
    }

    function getTrackingRole(privileges, is_primary) {
        let userTrackRole = userData.type + "-";
        if (is_primary) {
            userTrackRole += PRIMARY_ADMIN_LABEL;

            return preserveHyphenCamelCase(userTrackRole);
        }

        switch (userData.type) {
            case VENDOR:
                switch (privileges) {
                    case SALES:
                        userTrackRole += SALES_LABEL;
                        break;
                    case MARKETING:
                        userTrackRole += MARKETING_LABEL;
                        break;
                    case ADMIN:
                        userTrackRole += ADMIN_LABEL;
                        break;
                    case BILLING:
                        userTrackRole += BILLING_LABEL;
                        break;
                    default:
                        userTrackRole += NOT_FOUND;
                }
                break;
            case BROKER:
                switch (privileges) {
                    case USER:
                        userTrackRole += USER_LABEL;
                        break;
                    case USER_VIEW_ALL:
                        userTrackRole += USER_VIEW_ALL_LABEL;
                        break;
                    case ADMIN_BROKER:
                        userTrackRole += ADMIN_BROKER_LABEL;
                        break;
                    case USER_RESEARCH_ONLY:
                        userTrackRole += USER_RESEARCH_ONLY_LABEL;
                        break;
                    case PRIMARY_ADMIN:
                        userTrackRole += PRIMARY_ADMIN_LABEL;
                        break;
                    case PROJECT_ADMIN:
                        userTrackRole += PROJECT_ADMIN_LABEL;
                        break;
                    default:
                        userTrackRole += NOT_FOUND;
                }
                break;
            default:
                userTrackRole += "ShortlisterAdmin";
        }

        return preserveHyphenCamelCase(userTrackRole);
    }

    return (<div ref={userMenuRef}
                 className="sl-col-span-2 sl-user-menu sl-justify-self-end sl-font sl-font-medium sl-text-md sl-text-gray-400">
        <div onClick={toggleMenu} data-role={getTrackingRole(company?.privileges, userData.is_primary)}
             className="sl-font sl-cursor-pointer sl-font-medium sl-text-xs sl-inline-flex sl-user-dropdown-menu tr-user-role sl-mt-1 sl-mb-0">
            <UserAvatar userData={userData} isAvatarActive={isOpen}
                        avatarClass={'sl-h-10 sl-w-10 sl-bg-gray-100 sl-inline-flex sl-font sl-font-normal sl-text-lg sl-text-gray-600'}/>
            <div
                className={`sl-dropdown-menu sl-shadow-lg sl-max-w-64 ${isOpen ? 'sl-display-block' : ''} sl-z-3i`}>
                <div className="sl-dropdown-header">
                    <label className="sl-avatar-label-group sl-flex sl-mb-0">
                        <UserAvatar userData={userData} avatarClass={'sl-inline-flex sl-mr-0'}/>
                        <div className="sl-flex sl-flex-col sl-align-flex-start sl-pl-10 sl-overflow-hidden	">
                                <span
                                    className="sl-font sl-font-medium sl-text-sm sl-text-gray-700">
                                    {userData.first_name} {userData.last_name}
                                </span>
                            <span
                                className="sl-font sl-font-normal sl-text-xs sl-text-gray-base sl-ellipsis sl-w-fill-available">
                                    {userData.username}
                                </span>
                        </div>
                    </label>

                    {showUpgradePlan &&
                        <a className="sl-text-left sl-font sl-font-medium sl-text-xs sl-text-gray-700 sl-underline sl-mt-4 sl-block"
                           href={`${baseUrl}/plan-and-payment-settings/plans-and-pricing`}>Upgrade your plan</a>
                    }
                </div>
                <div className="sl-dropdown-content">
                    <ul className={'sl-m-0 sl-p-0'}>
                        {userMenu.map((menuItem, index) => (<li key={index}>
                            <IconLink icon={menuItem.icon} text={menuItem.text} href={menuItem.href} size={16}
                                      linkClass={'sl-inline-flex sl-align-center sl-btn sl-btn-md sl-btn-link-gray sl-text-sm sl-no-underline'}/>
                        </li>))}
                    </ul>
                </div>
            </div>
        </div>
    </div>);
};

export default UserMenu;
